import { atom, getDefaultStore, useAtom, useAtomValue } from "jotai";
import type { AssetType } from "@/domains/assets/api/types";

const DEFAULT_LABELS: Record<AssetType, string> = {
  camera: "Mobiele camera",
  mobileTree: "Mobile tree",
  camcar: "Camcar",
  batteryPack: "Batterij pack",
  trailer: "Trailer",
};

const BUILTIN_ICON_KEYS: Record<AssetType, string> = {
  camera: "camera",
  mobileTree: "trees",
  camcar: "truck",
  batteryPack: "battery",
  trailer: "container",
};

export type CustomAssetType = {
  code: string;
  label: string;
  iconKey: string;
};

export const assetTypeLabelsAtom = atom<Record<AssetType, string>>({ ...DEFAULT_LABELS });
export const customAssetTypesAtom = atom<ReadonlyArray<CustomAssetType>>([]);

function isBuiltInType(type: string): type is AssetType {
  return type in BUILTIN_ICON_KEYS;
}

export function getAssetTypeLabel(type: string): string {
  if (isBuiltInType(type)) {
    return getDefaultStore().get(assetTypeLabelsAtom)[type];
  }
  const found = getDefaultStore()
    .get(customAssetTypesAtom)
    .find((c) => c.code === type);
  return found?.label ?? type;
}

export function getAssetTypeIconKey(type: string): string {
  if (isBuiltInType(type)) return BUILTIN_ICON_KEYS[type];
  const found = getDefaultStore()
    .get(customAssetTypesAtom)
    .find((c) => c.code === type);
  return found?.iconKey ?? "box";
}

export function useAssetTypeLabel(type: string): string {
  const labels = useAtomValue(assetTypeLabelsAtom);
  const customs = useAtomValue(customAssetTypesAtom);
  if (isBuiltInType(type)) return labels[type];
  return customs.find((c) => c.code === type)?.label ?? type;
}

export function useAssetTypeLabels(): readonly [
  Record<AssetType, string>,
  (next: Record<AssetType, string>) => void,
] {
  const [labels, setLabels] = useAtom(assetTypeLabelsAtom);
  return [labels, setLabels] as const;
}

export function useCustomAssetTypes(): readonly [
  ReadonlyArray<CustomAssetType>,
  (next: ReadonlyArray<CustomAssetType>) => void,
] {
  const [list, setList] = useAtom(customAssetTypesAtom);
  return [list, setList] as const;
}

export const DEFAULT_ASSET_TYPE_LABELS = DEFAULT_LABELS;
